// our stuff:
import g_lang from './g_lang';

import {
    tempestAPI, tempestToken,
    rmsAPI,
    RDX,
    clientID,
    getClientId,
    sessionModelID,
} from './globals';
import { Progress } from './Progress';

type ResponseTypeMap = {
    'arraybuffer': ArrayBuffer,
    'blob': Blob,
    'document': Document,
    'text': string
}

type ContentType = /*string |*/ 'text/xml; charset=utf-8' | 'text/plain; charset=utf-8' | 'application/json; charset=utf-8' | 'application/octet-stream' | '';

/**
 * @class Comm
 * Communication helper wrapping XMLHttpRequest with:
 *  - connection allocation (simple concurrency limit)
 *  - automatic error processing
 *  - helper methods for GET/POST/RDX
 *  - progress reporting integration via `Progress`
 *
 * This class uses generics for `responseType` to provide typed responses
 * based on `ResponseTypeMap`.
 *
 */
class Comm {
    /** @brief Active XMLHttpRequest instance used for the current request.
     * @public
     * @type {XMLHttpRequest}
     */
    public xhr: XMLHttpRequest;

    /** @brief Last executed command URL (for error reporting).
     * @public
     * @type {string}
     */
    public lastCommand: string;

    /** @brief Optional progress reporter instance (mini UI).
     * @public
     * @type {Progress | undefined}
     */
    public progress: Progress | undefined;  // mini

    /** @brief Maximum number of concurrent connections allowed.
     * @protected
     * @type {number}
     */
    protected _connectionsLimit = 1;

    /** @brief Number of connections currently in use.
     * @protected
     * @type {number}
     */
    protected _connectionsUsed = 0;

    /** @brief Interval (ms) used when polling for free connection slot.
     * @protected
     * @type {number}
     */
    protected _connectionsCheckInterval = 5;

    /** @brief Create a new Comm instance.
     *
     * Sets up an internal `XMLHttpRequest` and attaches a progress handler
     * that forwards events to the optional `Progress` instance.
     *
     * @param {Progress} [progress] - Optional progress reporter.
     */
    constructor(progress?: Progress) {
        this.xhr = new XMLHttpRequest();
        this.xhr.onprogress = (ev) => this.onProgress(ev);

        this.lastCommand = ''; // last send commad

        this.progress = progress;
    }

    /** @brief Allocate a connection slot from the pool.
     *
     * If a slot is available the returned promise resolves immediately,
     * otherwise it polls `_connectionsCheckInterval` ms until a slot frees.
     *
     * @protected
     * @returns {Promise<void>} Promise that resolves when a connection slot is allocated.
     */
    protected allocateConnection(): Promise<void> {
        return new Promise<void>((resolve) : void => {
            // resolve immediately if we have connection that can be used
            if (this._connectionsUsed < this._connectionsLimit) {
                this._connectionsUsed++;
                resolve();
            }
            else {
                const checkInterval = setInterval(() : void => {
                    if (this._connectionsUsed < this._connectionsLimit) {
                        clearInterval(checkInterval);
                        this._connectionsUsed++;
                        resolve();
                    }
                }, this._connectionsCheckInterval);
            }
        })
    }

    /** @brief Release a previously allocated connection slot.
     *
     * Decrements internal `_connectionsUsed` counter. No underflow protection
     * is performed by design (caller must ensure correct usage).
     *
     * @protected
     * @returns {void}
     */
    protected freeConnection(): void {
        this._connectionsUsed--;
    }

    /** @brief Prepare a user-friendly server error string based on XHR response and HTTP status.
     *
     * - Converts line endings for `text/plain` responses.
     * - Strips common server markers like "500 Internal Server Error".
     * - Hides specific errors (e.g. "Ошибка 17") when appropriate.
     *
     * @protected
     * @param {XMLHttpRequest} xhr - The XMLHttpRequest instance that returned the error.
     * @param {string} message - Raw response text from the server.
     * @returns {string} Processed error message suitable for display.
     */
    protected static prepareServerErrorString(xhr: XMLHttpRequest, message: string): string {
        if (!message) {
            // Something wrong or empty xhr.response (502 Bad Gateway)
            return g_lang.t("IDS_COMMUNICATION_ERROR") + "<br />\r\n<small>HTTP Error status " + xhr.status.toString() + " " + xhr.statusText+"</small>";
        }

        const content_type = xhr.getResponseHeader("Content-Type");

        if (content_type && content_type.includes("text/plain")) { // rmsAPI
            //message = message.replace(/\r/g, '</br>');
            message = message.replace(/\n/g, '<br />');
        }

        if (xhr.status === 500) { // ZLP
            if (message.includes(" 17: ")) // Ошибка 17: Error 17: Process is interrupded by user
                return ""; // do not show this message to user
            message = message.replace(/500 Internal Server Error/g, "");
        }
        else if (xhr.status === 400) { // ZLP
            message = message.replace(/400 Bad Request/g, "");
        }
        else if (xhr.status === 403) { // ZLP
            message = message.replace(/403 Forbidden/g, "");
        }

        return message;
    }

    /** @brief Wrap `prepareServerErrorString` result into an `Error`.
     *
     * @protected
     * @param {string} message - Raw server message.
     * @returns {Error} Error object with processed message.
     */
    protected prepareServerError(message: string): Error {
        message = Comm.prepareServerErrorString(this.xhr, message);
        return new Error(message);
    }

    /** @brief Send a GET-like request by appending a cache-busting query parameter.
     *
     * Defaults to `arraybuffer` response type when not specified.
     *
     * @template ResponseType - Key of `ResponseTypeMap` describing response.
     * @param {string} url - Request URL.
     * @param {ResponseType} [responseType] - Desired response type key.
     * @returns {Promise<ResponseTypeMap[ResponseType]>} Promise resolving with the typed response.
     */
    async send<ResponseType extends keyof ResponseTypeMap = 'arraybuffer'>(url: string, responseType?: ResponseType) {
        // add some random parameter to prevent browser caching
        url += (url.includes('?') ? '&_cache=' : '?_cache=') + Date.now();
        return this.send_GET<ResponseType>(url, responseType);
    }

    /** @brief Send an RDX (XML) request. Chooses `arraybuffer` if payload starts with `<querybinary>`,
     * otherwise requests text.
     *
     * @param {string} xml - XML payload.
     * @returns {Promise<ArrayBuffer|string>} Promise resolving to ArrayBuffer for binary queries or text.
     */
    async send_RDX(xml: string) {
        const responseType: keyof ResponseTypeMap =
            xml.charAt(6) === 'b' ? // <querybinary>
                'arraybuffer' : 'text';
        return this.send_POST(RDX, xml, 'text/xml; charset=utf-8', responseType);
    }

    /** @brief Low-level HTTP sender used by the helpers.
     *
     * Handles:
     * - connection allocation and release
     * - XHR lifecycle and error mapping to `Error`
     * - header management (API keys, client/session ids)
     * - optional timeout
     *
     * @template ResponseType - Key of `ResponseTypeMap` describing response.
     * @param {string} method - HTTP method ('GET', 'POST', etc.).
     * @param {string} url - Request URL.
     * @param {Document|XMLHttpRequestBodyInit} body - Request body for methods like POST.
     * @param {ContentType} contentType - Content-Type header value or empty string to skip.
     * @param {ResponseType} responseType - Expected response type key.
     * @param {boolean} [bNoHeaders=false] - If true, skip standard API headers and set minimal headers.
     * @param {number} [timeout=0] - XHR timeout in milliseconds (0 = unlimited).
     * @returns {Promise<ResponseTypeMap[ResponseType]>} Promise resolving with the typed response.
     * @throws {Error} When a network error or server error status occurs.
     */
    async send_HTTP<ResponseType extends keyof ResponseTypeMap = 'arraybuffer'>(method: string, url: string, body: Document | XMLHttpRequestBodyInit, contentType: ContentType, responseType: ResponseType, bNoHeaders = false, timeout = 0) {
        this.lastCommand = url; // for error reporting

        //if (this.progress) {
        //    this.progress.clear(''/*_messagePrefix*/);
        //}

        if (!clientID) {
            getClientId(true);
        }

        await this.allocateConnection();

        let result: ResponseTypeMap[ResponseType];

        try {
            result = await new Promise<ResponseTypeMap[ResponseType]>((resolve, reject) : void => {
                // callback for any successfully completed network requests (including "error" HTTP status codes)
                this.xhr.onload =() : void => {
                    if (this.xhr.status / 100 >= 3) { // HTTP Status 3xx, 4xx, 5xx
                        if (this.xhr.responseType === 'text') {
                            reject(this.prepareServerError(this.xhr.responseText));
                        }
                        else if (this.xhr.responseType === 'arraybuffer' || this.xhr.responseType === 'blob') {
                            Comm.arrayBufferToString(this.xhr.response, 'UTF-8', (text: string) : void => {
                                 reject(this.prepareServerError(text));
                            });
                        }
                        else if (this.xhr.responseType === '' &&
                            this.xhr.responseText.substring(0, 5).toLowerCase() === "<html") { // POST???
                            reject(this.prepareServerError(this.xhr.responseText));
                        }
                        else {
                            reject(new Error("Error status " +this.xhr.status.toString()));
                        }
                    }
                    else {
                        resolve(this.xhr.response);
                    }
                };

                // callback for any network errors (_not_ HTTP status codes)
                this.xhr.onerror = function (event: ProgressEvent<EventTarget>) : void {
                    if (event && event.target)
                        reject(new Error(g_lang.t("IDS_COMMUNICATION_ERROR")/*+ (this.xhr.status? this.xhr.status: '')*/));
                    else
                        reject(new Error("Network error occured."));
                }

                this.xhr.ontimeout = function (event) : void {
                    // XMLHttpRequest timed out. Do something here.
                    reject(new Error("Timeout. Server does not responds."));
                };

                this.xhr.open(method, encodeURI(url), true);

                if (responseType)
                    this.xhr.responseType = responseType;
                else
                    this.xhr.responseType = 'arraybuffer';

                if (!bNoHeaders && tempestAPI) {
                    this.xhr.setRequestHeader("Authorization", tempestToken); // TEMPEST API key
                }

                if (!bNoHeaders && !tempestAPI && !rmsAPI) {
                    this.xhr.setRequestHeader("apiClientID", clientID);
                    this.xhr.setRequestHeader("apiSessionModelID", sessionModelID);

                    /*if (!g_from_FILE) { // Chrome refuse to set unsafe header "Cookie".
                             // "Access from origin 'null' has been blocked by CORS policy" - он валит все на сервер, хотя сервер все ответил верно.
                        //this.xhr.withCredentials = true; // for Cookies
                        // this.xhr.setRequestHeader("Cookie", "browserID=" + browserID); Forbidden header name (https://developer.mozilla.org/en-US/docs/Glossary/Forbidden_header_name)!
                    }*/
                }

                if (bNoHeaders) {
                    this.xhr.setRequestHeader("User-Agent", "ProPlast 10");
                    this.xhr.withCredentials = false; // for CORS
                }

                this.xhr.timeout = timeout;// 2000; // time in milliseconds (0 - infinite timeout)

                if (contentType)
                    this.xhr.setRequestHeader("Content-Type", contentType);

                this.xhr.send(body);
            });
        }
        finally {
            this.freeConnection();
        }

        return result;
    }

    /** @brief Convenience GET sender.
     *
     * @template ResponseType - Key of `ResponseTypeMap`.
     * @param {string} url - Request URL.
     * @param {ResponseType} [responseType] - Optional response type key.
     * @returns {Promise<ResponseTypeMap[ResponseType]>} Promise resolving to the typed response.
     */
    async send_GET<ResponseType extends keyof ResponseTypeMap = 'arraybuffer'>(url: string, responseType?: ResponseType) {
        return this.send_HTTP<ResponseType>('GET', url, '', '', responseType);
    }

    /** @brief Convenience POST sender.
     *
     * @template ResponseType - Key of `ResponseTypeMap`.
     * @param {string} url - Request URL.
     * @param {Document|XMLHttpRequestBodyInit} body - Request body.
     * @param {ContentType} contentType - Content-Type header value.
     * @param {ResponseType} responseType - Desired response type key.
     * @returns {Promise<ResponseTypeMap[ResponseType]>} Promise resolving to the typed response.
     */
    async send_POST<ResponseType extends keyof ResponseTypeMap = 'arraybuffer'>(url: string, body: Document | XMLHttpRequestBodyInit, contentType: ContentType, responseType: ResponseType) {
        return this.send_HTTP<ResponseType>('POST', url, body, contentType, responseType);
    }

    /** @brief XHR onprogress handler that updates the attached `Progress` instance.
     *
     * Calculates a percentage and sets final flag when loaded equals total.
     *
     * @param {ProgressEvent} event - Progress event from XHR.
     * @returns {void}
     */
    onProgress(event: ProgressEvent) : void {
        if (!this.progress)
            return;

        const percent = Math.round(event.loaded * 100 / (1 + event.total)) * 1;
        this.progress.set(percent, event.loaded === event.total);
    }

    /** Convert an ArrayBuffer/Blob-like `buffer` to string using a FileReader.
     *
     * Useful to decode error responses when `responseType` was `arraybuffer` or `blob`.
     *
     * @static
     * @param {BlobPart} buffer - Buffer or blob-like part to convert.
     * @param {string} encoding - Text encoding to use (e.g. 'UTF-8').
     * @param {(text: string) => void} callback - Callback invoked with the decoded text.
     * @returns {void}
     */
    static arrayBufferToString(buffer: BlobPart, encoding: string, callback: (text: string/* | ArrayBuffer*/) => void) {
        const blob = new Blob([buffer], { type: 'text/plain' });
        if (!blob.size) {
            callback(''); // empty this.xhr.response (502 Bad Gateway)
            return;
        }
        const reader = new FileReader();
        reader.onload = function (evt) {
            callback(evt.target.result as string);
        };
        reader.readAsText(blob, encoding);
    }
}

export { Comm };